import React, { useState, useEffect, useMemo } from 'react';
import {
  LayoutDashboard, Users, Table, Upload, Settings, Target, TrendingUp, Calculator,
  Layers, LogOut, Scale, Database, Clock, ShieldCheck, AlertTriangle, Building,
  Sparkles, Smile, Crosshair, Megaphone, FileText, User, Crown, Network, Trophy,
  BarChart3, Lock, ChevronDown, ChevronRight, DollarSign, Briefcase,
} from 'lucide-react';

// Category-based accordion nav.  Two always-visible primary items (TK Analyst,
// Dashboard), everything else is grouped so the primary column stays scannable.
//
// Each section persists its open/closed state in localStorage per key. When
// the user navigates to any view inside a section, that section auto-expands
// (but doesn't force-close others). Beta Performance is closed by default
// because it holds sensitive perf-rating pages that shouldn't sit permanently
// in the primary chrome.
const NAV_SECTIONS = [
  {
    key: 'revenue', label: 'Revenue', icon: DollarSign, defaultOpen: false,
    items: [
      { view: 'revenue-tracker',  label: 'Revenue Tracker',  icon: TrendingUp, badge: 'NEW' },
      { view: 'client-ladder',    label: 'Revenue Detail',   icon: Trophy,     badge: 'NEW' },
      { view: 'revenue-timeline', label: 'Revenue Timeline', icon: ShieldCheck },
      { view: 'revenue-per-lot',  label: 'Revenue per Lot',  icon: Building },
    ],
  },
  {
    key: 'portfolio', label: 'Portfolio', icon: Briefcase, defaultOpen: false,
    items: [
      { view: 'analysis',        label: 'Plan Analysis',        icon: Table },
      { view: 'plan-groups',     label: 'OC Plan Groups',       icon: Layers },
      { view: 'deep-dive',       label: 'Group Deep Dive',      icon: Crosshair, badge: 'SWOT' },
      { view: 'ideal-client',    label: 'Ideal Client Profile', icon: Target },
      { view: 'nps-analysis',    label: 'NPS Analytics',        icon: Smile },
      { view: 'risk-register',   label: 'Risk Register',        icon: AlertTriangle },
    ],
  },
  {
    key: 'people', label: 'People', icon: Users, defaultOpen: false,
    items: [
      { view: 'managers',           label: 'Manager Profiling',        icon: Users },
      { view: 'workload-analysis',  label: 'Workload Analysis',        icon: BarChart3, badge: 'NEW' },
      { view: 'workload-agent',     label: 'Workload Decision Agent',  icon: Scale, badge: 'AI' },
      { view: 'team-structure',     label: 'Team Structure',           icon: Network, badge: 'NEW' },
    ],
  },
  {
    key: 'planning', label: 'Planning', icon: Calculator, defaultOpen: false,
    items: [
      { view: 'what-if',       label: 'What-If Analyzer',    icon: Calculator },
      { view: 'plan-config',   label: 'Plan Configuration',  icon: TrendingUp },
      { view: 'plan-timeline', label: 'Plan Timeline',       icon: Clock },
    ],
  },
  {
    key: 'marketing', label: 'Marketing', icon: Megaphone, defaultOpen: false,
    items: [
      { view: 'value-proposition', label: 'TK Value Proposition', icon: Megaphone, badge: 'NEW' },
      { view: 'proposal',          label: 'Committee Proposal',   icon: FileText,  badge: 'NEW' },
    ],
  },
  {
    key: 'data', label: 'Data & Tools', icon: Database, defaultOpen: false,
    items: [
      { view: 'upload',       label: 'Data Upload',   icon: Upload },
      { view: 'data-quality', label: 'Data Quality',  icon: Database },
      { view: 'settings',     label: 'Settings',      icon: Settings },
    ],
  },
  {
    key: 'beta', label: 'Beta Performance', icon: Lock, defaultOpen: false, gated: true, hidden: true,
    items: [
      { view: 'performance-ladder',    label: 'Performance Ladder',    icon: Trophy },
      { view: 'manager-deep-dive',     label: 'Manager Deep Dive',     icon: User },
      { view: 'team-leader-deep-dive', label: 'Team Leader Deep Dive', icon: Crown },
    ],
  },
];

const STORAGE_PREFIX = 'tk_nav_section_';

const Sidebar = ({ currentView, setCurrentView, onLogout }) => {
  // Map view → sectionKey so we can auto-open on load / nav change.
  const viewToSection = useMemo(() => {
    const map = {};
    NAV_SECTIONS.forEach(s => s.items.forEach(i => { map[i.view] = s.key; }));
    return map;
  }, []);

  // Per-section open state — restore from localStorage, default false.
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    NAV_SECTIONS.forEach(s => {
      try {
        const stored = localStorage.getItem(STORAGE_PREFIX + s.key);
        initial[s.key] = stored === null ? s.defaultOpen : stored === 'true';
      } catch { initial[s.key] = s.defaultOpen; }
    });
    return initial;
  });

  // Auto-open whichever section owns the current view.
  useEffect(() => {
    const sectionKey = viewToSection[currentView];
    if (sectionKey && !openSections[sectionKey]) {
      setOpenSections(prev => ({ ...prev, [sectionKey]: true }));
      try { localStorage.setItem(STORAGE_PREFIX + sectionKey, 'true'); } catch { /* noop */ }
    }
  }, [currentView, viewToSection, openSections]);

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STORAGE_PREFIX + key, String(next[key])); } catch { /* noop */ }
      return next;
    });
  };

  const isSectionActive = (section) => section.items.some(i => i.view === currentView);

  return (
    <aside data-testid="sidebar" className="w-60 h-screen bg-[#0A0A0A] border-r border-[#1A1A1A] flex flex-col fixed left-0 top-0 z-30">
      {/* Logo */}
      <div className="px-4 py-5 border-b border-[#1A1A1A]">
        <div className="flex flex-col items-center text-center gap-2">
          <img
            src="/the-knight-logo.png"
            alt="The Knight"
            data-testid="sidebar-logo"
            className="w-24 h-24 object-contain"
            style={{ filter: 'invert(1) brightness(1.05)' }}
          />
          <p className="text-[#A3A3A3] text-[10px] font-semibold uppercase tracking-[0.2em] mt-1">TK Analytics</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {/* Primary — always visible */}
        <NavButton active={currentView === 'tk-analyst'} icon={Sparkles} label="TK Analyst" onClick={() => setCurrentView('tk-analyst')} badge="AI" />
        <NavButton active={currentView === 'dashboard'} icon={LayoutDashboard} label="Dashboard" onClick={() => setCurrentView('dashboard')} />

        {/* Sections */}
        <div className="mt-4 space-y-0.5">
          {NAV_SECTIONS.filter(s => !s.hidden).map(section => (
            <AccordionSection
              key={section.key}
              section={section}
              open={openSections[section.key]}
              onToggle={() => toggleSection(section.key)}
              currentView={currentView}
              setCurrentView={setCurrentView}
              hasActive={isSectionActive(section)}
            />
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-[#1A1A1A]">
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium text-[#737373] hover:text-red-400 hover:bg-red-500/5 transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};

const AccordionSection = ({ section, open, onToggle, currentView, setCurrentView, hasActive }) => {
  const Icon = section.icon;
  const chipCount = section.items.length;
  return (
    <div data-testid={`nav-section-${section.key}`}>
      <button
        data-testid={`nav-section-toggle-${section.key}`}
        onClick={onToggle}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-semibold uppercase tracking-[0.06em] transition-colors
          ${hasActive
            ? 'text-white bg-white/[0.03]'
            : 'text-[#A3A3A3] hover:text-white hover:bg-white/[0.04]'}
          ${section.gated ? 'opacity-90' : ''}`}
      >
        <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} />
        <span className="truncate">{section.label}</span>
        <span className="ml-auto flex items-center gap-1.5">
          {hasActive && !open && (
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Active view inside" />
          )}
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-white/5 text-white/40">{chipCount}</span>
          {open
            ? <ChevronDown className="w-3 h-3 text-[#525252]" strokeWidth={2} />
            : <ChevronRight className="w-3 h-3 text-[#525252]" strokeWidth={2} />}
        </span>
      </button>
      {open && (
        <div data-testid={`nav-section-panel-${section.key}`} className="border-l border-white/[0.05] ml-4 pl-1 pb-1 mt-0.5">
          {section.items.map(item => (
            <NavButton
              key={item.view}
              active={currentView === item.view}
              icon={item.icon}
              label={item.label}
              onClick={() => setCurrentView(item.view)}
              badge={item.badge}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
};

const NavButton = ({ active, icon: Icon, label, onClick, badge, nested }) => (
  <button
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    onClick={onClick}
    className={`w-full flex items-center gap-3 ${nested ? 'pl-6 pr-3' : 'px-3'} py-2 rounded-lg text-[13px] font-medium transition-colors relative
      ${active
        ? 'bg-white/[0.08] text-white'
        : 'text-[#A3A3A3] hover:text-white hover:bg-white/[0.04]'
      }`}
  >
    <Icon className={`${nested ? 'w-3.5 h-3.5' : 'w-4 h-4'} flex-shrink-0`} strokeWidth={1.5} />
    <span className="truncate">{label}</span>
    {badge && (
      <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/10 text-white/60">
        {badge}
      </span>
    )}
    {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-white rounded-r-full" />}
  </button>
);

export default Sidebar;
